import { useCallback, useEffect, useRef, useState } from 'react';

export const AI_DRAWER_WIDTH_KEY = 'pratus-ai-drawer-width';
export const AI_DRAWER_MIN_WIDTH = 360;
export const AI_DRAWER_DEFAULT_WIDTH = 440;
/** Never let the drawer swallow the whole viewport on a desktop layout. */
export const AI_DRAWER_MAX_VIEWPORT_RATIO = 0.7;

export const clampDrawerWidth = (
  width: number,
  viewportWidth: number = typeof window !== 'undefined'
    ? window.innerWidth
    : 1440
): number => {
  const max = Math.max(
    AI_DRAWER_MIN_WIDTH,
    Math.floor(viewportWidth * AI_DRAWER_MAX_VIEWPORT_RATIO)
  );
  if (!Number.isFinite(width)) return AI_DRAWER_DEFAULT_WIDTH;
  return Math.min(max, Math.max(AI_DRAWER_MIN_WIDTH, Math.round(width)));
};

export const readStoredDrawerWidth = (): number => {
  if (typeof window === 'undefined') return AI_DRAWER_DEFAULT_WIDTH;
  try {
    const raw = window.localStorage.getItem(AI_DRAWER_WIDTH_KEY);
    return clampDrawerWidth(raw ? Number(raw) : AI_DRAWER_DEFAULT_WIDTH);
  } catch {
    return AI_DRAWER_DEFAULT_WIDTH;
  }
};

export interface DrawerWidthController {
  width: number;
  isResizing: boolean;
  /** Attach to the drag handle's `onPointerDown`. */
  startResize: (event: React.PointerEvent<HTMLElement>) => void;
  /** Keyboard resize for the handle (`ArrowLeft`/`ArrowRight`). */
  onHandleKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
}

/**
 * Resizable drawer width persisted to localStorage. The drawer is anchored to
 * the right edge, so dragging the left-edge handle leftwards grows it.
 */
export const useDrawerWidth = (): DrawerWidthController => {
  const [width, setWidth] = useState<number>(readStoredDrawerWidth);
  const [isResizing, setIsResizing] = useState(false);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(AI_DRAWER_WIDTH_KEY, String(width));
    } catch {
      // localStorage unavailable (private mode) — width is session-only.
    }
  }, [width]);

  useEffect(() => {
    if (!isResizing) return undefined;
    const onMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const delta = drag.startX - event.clientX;
      setWidth(clampDrawerWidth(drag.startWidth + delta));
    };
    const stop = () => {
      dragRef.current = null;
      setIsResizing(false);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };
  }, [isResizing]);

  const startResize = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (event.button !== 0) return;
      event.preventDefault();
      dragRef.current = { startX: event.clientX, startWidth: width };
      setIsResizing(true);
    },
    [width]
  );

  const onHandleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      const step = event.shiftKey ? 64 : 16;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setWidth((w) => clampDrawerWidth(w + step));
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        setWidth((w) => clampDrawerWidth(w - step));
      } else if (event.key === 'Home') {
        event.preventDefault();
        setWidth(AI_DRAWER_DEFAULT_WIDTH);
      }
    },
    []
  );

  return { width, isResizing, startResize, onHandleKeyDown };
};
